import { configureStore } from '@reduxjs/toolkit';
import type { AuthClient } from 'features/auth/AuthClient';
import { authStateChanged, restoreSession } from 'features/auth/authSlice';

import { apiMiddleware, rootReducer } from './reducers';

export type RootState = ReturnType<typeof rootReducer>;

type CreateAppStoreOptions = {
    authClient: AuthClient;
    preloadedState?: Partial<RootState>;
    initializeAuth?: boolean;
};

export const createAppStore = ({ authClient, preloadedState, initializeAuth = true }: CreateAppStoreOptions) => {
    const store = configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({ thunk: { extraArgument: authClient } }).concat(apiMiddleware),
        preloadedState,
    });

    const unsubscribeFromAuth = initializeAuth
        ? authClient.subscribe?.(user => store.dispatch(authStateChanged(user)))
        : undefined;

    if (initializeAuth) {
        void store.dispatch(restoreSession());
    }

    return Object.assign(store, {
        disposeAuth: () => {
            unsubscribeFromAuth?.();
        },
    });
};

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore['dispatch'];
